import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar, closeSnackbar } from 'notistack';
// @mui
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Unstable_Grid2";
import {useTheme} from '@mui/material/styles';
import { Box, Container } from '@mui/system';
import { Button, Card, CardContent, Typography } from '@mui/material';
// components
import { useSettingsContext } from '../../components/settings';
import { getFinancialData } from '../../components/upload';
import Iconify from '../../components/iconify';

import ChartProfile from './charts/chart-profile';
import ChartProfileSingle from './charts/chart-profile-single';
import ChartProfileSingleDeactivated from './charts/chart-profile-single-deactivated';

import FinancialService from '../../services/financial';

export default function FInancialConductProfile() {
    const navigate = useNavigate();
    const settings = useSettingsContext()
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    
    const [resumen, setResumen] = useState(null);
    const [processed, setProcessed] = useState(false);
    const [loading, setLoading] = useState(() => settings.conductProcessing || null);

    useEffect(() => {

        try {
            const data = getFinancialData();
            if (!data) throw new Error('No data');
            setResumen(data);
            settings.onFiles(); // Solo si tienes datos
            settings.onValid();

            if (data.perfil.apertura === "No procesado") {
                setProcessed(false);
            } else {
                setProcessed(true);
            }
        } catch (error) {
            console.warn('No se encontraron datos. Redirigiendo al inicio...');
            navigate('/financial');
        }
    }, [navigate, settings]);
    
    const handleProcessData = useCallback(
    async (data) => {
        if (!data) return;

        setLoading(true);
        settings.setConductProcessing(true)

        const snackbarId = enqueueSnackbar(`Se está procesando los datos`, { variant: 'info', persist: true });

        try {
            const response = await FinancialService.getProfile(data); 

            console.log(response);

            const jsonData = response;
            localStorage.setItem("financeData", JSON.stringify(jsonData));
            setResumen(getFinancialData());

            setProcessed(true);
            setLoading(false);
            settings.setConductProcessing(false)

        closeSnackbar(snackbarId);
        } catch (error) {
            closeSnackbar(snackbarId);
            setLoading(false);
            settings.setConductProcessing(false)
            enqueueSnackbar("Error procesando el resumen", { variant: 'error' });
            console.error("Error procesando resumen:", error);
        }
        
    },[enqueueSnackbar, settings]);

    if (!resumen) return null;


    return (
        <Container maxWidth='xl'>
            <Grid container spacing={2}>
                <Grid xs={12}>
                    {processed ? 
                        <ChartProfile
                            title="Perfil conductual"
                            chart={{
                                series: [
                                    { label: 'Apertura y experiencia', value: Math.floor(resumen.perfil.apertura) },
                                    { label: 'Planificación y control', value: Math.floor(resumen.perfil.planificador_control) },
                                    { label: 'Consumo social', value: Math.floor(resumen.perfil.consumo_social) },
                                    { label: 'Impulso emocional', value: Math.floor(resumen.perfil.impulsivo_emocional) },
                                ],
                            }}
                        />
                        :
                        <Card>
                            <CardContent>
                                <Typography variant="h3" fontWeight="bold" align='center' marginBottom={2}>Perfil conductual</Typography>
                                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="uim:process" width={30} />}
                                        onClick={() => handleProcessData(localStorage.getItem('financeData'))}
                                        disabled={loading}
                                        >
                                        Procesar
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    }
                </Grid>
                <Grid xs={12}>
                    {processed ? <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} width="100%">
                        <ChartProfileSingle
                            chart={{
                                series: Math.floor(resumen.perfil.apertura)
                            }}
                            color={theme.palette.simplai.array[0]}
                            title="Apertura y experiencia"
                            icon="fluent:book-open-globe-20-filled"
                            tooltip="Este perfil describe a personas con una mente abierta y una fuerte curiosidad. En este sentido, muestran interés en aprender y en viajar, además de buscar la experimentación e innovación constantemente."
                            
                        />
                        <ChartProfileSingle
                            chart={{
                                series: Math.floor(resumen.perfil.planificador_control)
                            }}
                            color={theme.palette.simplai.array[1]}
                            title="Planificación y control"
                            icon="ant-design:control-outlined"
                            tooltip="Este perfil describe a personas organizadas y con gran capacidad de control. En este sentido, planifican de forma deliberada antes de actuar, además de buscar la estabilidad y el orden en sus vidas."
                            
                        />
                        <ChartProfileSingle
                            chart={{
                                series: Math.floor(resumen.perfil.consumo_social)
                            }}
                            color={theme.palette.simplai.array[2]}
                            title="Consumo social"
                            icon="foundation:social-skillshare"
                            tooltip="Este perfil describe a personas expresivas y con grandes dotes sociales. En este sentido, interactúan activamente con otras personas, además de buscar el reconocimiento y la pertenencia a un grupo"                            
                        />
                        <ChartProfileSingle
                            chart={{
                                series: Math.floor(resumen.perfil.impulsivo_emocional)
                            }}
                            color={theme.palette.simplai.array[3]}
                            title="Impulso emocional"
                            icon="healthicons:crisis-response-center-person"
                            tooltip="Este perfil describe a personas de alta impulsividad emocional. En este sentido, toman decisiones y actúan rápidamente ante cambios externos, además de buscar resultados positivos a corto plazo"
                            
                        />
                    </Stack>
                    :
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} width="100%">
                        <ChartProfileSingleDeactivated
                            chart={{
                                series: Math.floor(resumen.perfil.apertura)
                            }}
                            color={theme.palette.simplai.array[0]}
                            title="Apertura y experiencia"
                            icon="fluent:book-open-globe-20-filled"
                            tooltip="Este perfil describe a personas con una mente abierta y una fuerte curiosidad. En este sentido, muestran interés en aprender y en viajar, además de buscar la experimentación e innovación constantemente."
                          
                        />
                        <ChartProfileSingleDeactivated
                            chart={{
                                series: Math.floor(resumen.perfil.planificador_control)
                            }}
                            color={theme.palette.simplai.array[1]}
                            title="Planificación y control"
                            icon="ant-design:control-outlined"
                            tooltip="Este perfil describe a personas organizadas y con gran capacidad de control. En este sentido, planifican de forma deliberada antes de actuar, además de buscar la estabilidad y el orden en sus vidas."
                            
                        />
                        <ChartProfileSingleDeactivated
                            chart={{
                                series: Math.floor(resumen.perfil.consumo_social)
                            }}
                            color={theme.palette.simplai.array[2]}
                            title="Consumo social"
                            icon="foundation:social-skillshare"
                            tooltip="Este perfil describe a personas expresivas y con grandes dotes sociales. En este sentido, interactúan activamente con otras personas, además de buscar el reconocimiento y la pertenencia a un grupo"                            
                            
                        />
                        <ChartProfileSingleDeactivated
                            chart={{
                                series: Math.floor(resumen.perfil.impulsivo_emocional)
                            }}
                            color={theme.palette.simplai.array[3]}
                            title="Impulso emocional"
                            icon="healthicons:crisis-response-center-person"
                            tooltip="Este perfil describe a personas de alta impulsividad emocional. En este sentido, toman decisiones y actúan rápidamente ante cambios externos, además de buscar resultados positivos a corto plazo"
                            
                        />
                    </Stack>
                    }
                </Grid>
            </Grid>
        </Container>
    )
}